import React from "react";
import { Admin, CustomRoutes, Resource } from "react-admin";
import { BrowserRouter, Route } from "react-router-dom";
import { StyledEngineProvider } from "@mui/material/styles";
import {
  ManageAccounts as AccountsIcon,
  Science as TestIcon,
  Payments as CreditConsumptionIcon,
  People as UserManagementIcon,
  AdminPanelSettings as PlatformUsersIcon,
  EventNote as ScheduledJobsIcon,
  BarChart as StatisticsIcon,
  NotificationsNone as NotificationLogsIcon,
  Apps as ApplicationsIcon,
  VpnKey as LicensesIcon,
  Business as ProvidersIcon,
  PriceChange as DefaultPricingIcon,
  Groups as ManageGroupsIcon,
  Backup as BackupIcon,
} from "@mui/icons-material";

// Providers
import { authProvider, dataProvider, i18nProvider } from "./providers";

// Layout
import { CustomLayout, RgNotification } from "./layouts";
import CustomLoginPage from "./components/auth/CustomLoginPage";

// Theme
import lightTheme from "./lightTheme";
import darkTheme from "./darkTheme";

// Main-sidebar features (placeholders except Accounts — Applications has full CRUD)
import { AccountsList, AccountCreate, AccountEdit } from "./features/accounts";
import {
  TestList,
  EmailTestPage,
  SmsTestPage,
  WhatsappTestPage,
  TelegramTestPage,
  WebPushTestPage,
  AppPushTestPage,
  TeamChatTestPage,
  BotChatTestPage,
  InboxTestPage,
  WebhookTestPage,
} from "./features/test";
import { CreditConsumptionList } from "./features/credit-consumption";
import { CostCenterList } from "./features/cost-center";
import { PricingMatrix } from "./features/pricing";
import { CreditBalanceList } from "./features/credit-balance";
import { CreditTransactionList } from "./features/credit-transaction";
import { CreditEventLogsList } from "./features/credit-event-logs";
import { CreditEventSummaryList } from "./features/credit-event-summary";
import { UserManagementList } from "./features/user-management";
import { PlatformUserList } from "./features/platform-users";
import { ScheduledJobsList } from "./features/scheduled-jobs";
import { StatisticsDashboard } from "./features/statistics";
import { NotificationLogsList } from "./features/notification-logs";

// Settings (user-menu) features
import {
  ApplicationsList,
} from "./features/applications";
import { LicensesList } from "./features/licenses";
import { ProvidersList } from "./features/providers";
import { DefaultPricingPage } from "./features/default-pricing";
import { ManageGroupsList, ManageGroupsEditor } from "./features/manage-groups";
import { BackupPage } from "./features/backup";

// Non-Resource features (custom routes)
import { UserProfilePage } from "./features/user-profile";
import { ConfigurationPage, DefaultParametersPage } from "./features/settings";

const App: React.FC = () => {
  // Force light theme as the default at app boot. The AppBar toggle still
  // lets the user switch to dark mid-session (persisted in RaStore), but
  // logout clears that preference so the next login lands in light again.

  return (
    <StyledEngineProvider injectFirst>
      <BrowserRouter>
        <Admin
          authProvider={authProvider}
          dataProvider={dataProvider}
          i18nProvider={i18nProvider}
          theme={lightTheme}
          darkTheme={darkTheme}
          defaultTheme="light"
          layout={CustomLayout}
          notification={RgNotification}
          loginPage={CustomLoginPage}
          requireAuth
          disableTelemetry
        >
          {/* Main sidebar resources (order matters — first one is the landing route) */}
          <Resource
            name="apps/accounts"
            list={AccountsList}
            create={AccountCreate}
            edit={AccountEdit}
            icon={AccountsIcon}
            recordRepresentation={(r) => r?.accountResponse?.accountName ?? ""}
          />
          <Resource name="test" list={TestList} icon={TestIcon} />
          <Resource
            name="credit-consumption"
            list={CreditConsumptionList}
            icon={CreditConsumptionIcon}
          />
          <Resource
            name="credit-consumption/cost-center"
            list={CostCenterList}
            recordRepresentation="name"
          />
          <Resource
            name="credit-consumption/pricing"
            list={PricingMatrix}
            recordRepresentation="costCenterName"
          />
          <Resource
            name="credit-consumption/credit-balance"
            list={CreditBalanceList}
            recordRepresentation="costCenterName"
          />
          <Resource
            name="credit-consumption/credit-transaction"
            list={CreditTransactionList}
            recordRepresentation="costCenterName"
          />
          <Resource
            name="credit-consumption/credit-event-logs"
            list={CreditEventLogsList}
            recordRepresentation="costCenterName"
          />
          <Resource
            name="credit-consumption/credit-event-summary"
            list={CreditEventSummaryList}
            recordRepresentation="costCenterName"
          />
          <Resource
            name="user-management"
            list={UserManagementList}
            icon={UserManagementIcon}
          />
          <Resource
            name="platform-users"
            list={PlatformUserList}
            icon={PlatformUsersIcon}
          />
          <Resource
            name="scheduled-jobs"
            list={ScheduledJobsList}
            icon={ScheduledJobsIcon}
          />
          <Resource
            name="statistics"
            list={StatisticsDashboard}
            icon={StatisticsIcon}
          />
          <Resource
            name="notification-logs"
            list={NotificationLogsList}
            icon={NotificationLogsIcon}
          />

          {/* Settings — reached via the sidebar user-chip popover, not the nav */}
          <Resource
            name="applications"
            list={ApplicationsList}
            icon={ApplicationsIcon}
            recordRepresentation="name"
          />
          <Resource
            name="licenses"
            list={LicensesList}
            icon={LicensesIcon}
            recordRepresentation="name"
          />
          <Resource
            name="providers"
            list={ProvidersList}
            icon={ProvidersIcon}
            recordRepresentation="providerName"
          />
          <Resource
            name="default-pricing"
            list={DefaultPricingPage}
            icon={DefaultPricingIcon}
            recordRepresentation="communicationChannel"
          />
          <Resource
            name="manage-groups"
            list={ManageGroupsList}
            icon={ManageGroupsIcon}
          />
          {/* SuperUser-only DB backup/restore. Reached from the user-chip
              popover; the backend enforces the SuperUser 401/403 too. */}
          <Resource name="backup" list={BackupPage} icon={BackupIcon} />

          {/* Non-Resource pages — custom routes. Linked from the user-chip popover. */}
          <CustomRoutes>
            <Route path="/user-profile" element={<UserProfilePage />} />
            <Route path="/settings/configuration" element={<ConfigurationPage />} />
            <Route path="/settings/default-parameters" element={<DefaultParametersPage />} />
            <Route path="/manage-groups/edit" element={<ManageGroupsEditor />} />
            {/* Notification test pages — sidebar submenu under Test */}
            <Route path="/test/email" element={<EmailTestPage />} />
            <Route path="/test/sms" element={<SmsTestPage />} />
            <Route path="/test/whatsapp" element={<WhatsappTestPage />} />
            <Route path="/test/telegram" element={<TelegramTestPage />} />
            <Route path="/test/web-push" element={<WebPushTestPage />} />
            <Route path="/test/app-push" element={<AppPushTestPage />} />
            <Route path="/test/team-chat" element={<TeamChatTestPage />} />
            <Route path="/test/viber" element={<BotChatTestPage channel="viber" />} />
            <Route path="/test/line" element={<BotChatTestPage channel="line" />} />
            <Route path="/test/inbox" element={<InboxTestPage />} />
            <Route path="/test/webhook" element={<WebhookTestPage />} />
          </CustomRoutes>
        </Admin>
      </BrowserRouter>
    </StyledEngineProvider>
  );
};

export default App;
